import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import RichTextEditor from './components/RichTextEditor';

interface StoredUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  password: string;
}

interface Post {
  id: string;
  titleUz: string;
  titleRu: string;
  contentUz: string;
  contentRu: string;
  category: 'news' | 'announcement' | 'event';
  imageUrl: string;
  publishedAt: string;
  createdAt: string;
  isPublished: boolean;
}

const ADMIN_EMAIL = 'medivoyage.uz@gmail.com';
const ADMIN_PASSWORD = '!!Mila1464';
const ADMIN_SESSION_KEY = 'medtour_admin_session';
const USERS_LIST_KEY = 'medtour_users_list';
const POSTS_LIST_KEY = 'medtour_posts_list';

type TabType = 'users' | 'posts';

const CATEGORY_LABELS: Record<Post['category'], { uz: string; ru: string }> = {
  news: { uz: 'Yangilik', ru: 'Новость' },
  announcement: { uz: 'E\'lon', ru: 'Объявление' },
  event: { uz: 'Tadbir', ru: 'Мероприятие' },
};

const CATEGORY_COLORS: Record<Post['category'], string> = {
  news: 'bg-teal-100 text-teal-700',
  announcement: 'bg-orange-100 text-orange-700',
  event: 'bg-purple-100 text-purple-700',
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState<'uz' | 'ru'>('uz');
  const [users, setUsers] = useState<StoredUser[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('users');

  // Post form state
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [postTitleUz, setPostTitleUz] = useState('');
  const [postTitleRu, setPostTitleRu] = useState('');
  const [postContentUz, setPostContentUz] = useState('');
  const [postContentRu, setPostContentRu] = useState('');
  const [postCategory, setPostCategory] = useState<Post['category']>('news');
  const [postImageUrl, setPostImageUrl] = useState('');
  const [postPublishedAt, setPostPublishedAt] = useState('');
  const [postIsPublished, setPostIsPublished] = useState(true);
  const [postFormError, setPostFormError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [postSuccess, setPostSuccess] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const session = localStorage.getItem(ADMIN_SESSION_KEY);
    if (session === 'authenticated') setIsAuthenticated(true);
    const browserLang = navigator.language;
    if (browserLang.startsWith('ru')) setLang('ru');
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadUsers();
      loadPosts();
    }
  }, [isAuthenticated]);

  const loadUsers = () => {
    const stored = localStorage.getItem(USERS_LIST_KEY);
    if (stored) {
      try { setUsers(JSON.parse(stored)); } catch { setUsers([]); }
    }
  };

  const loadPosts = () => {
    const stored = localStorage.getItem(POSTS_LIST_KEY);
    if (stored) {
      try { 
        const loadedPosts = JSON.parse(stored);
        // Ensure all posts have isPublished field
        const postsWithPublished = loadedPosts.map((post: Post) => ({
          ...post,
          isPublished: post.isPublished !== undefined ? post.isPublished : true
        }));
        setPosts(postsWithPublished);
      } catch { 
        setPosts([]); 
      }
    }
  };

  const t = (uz: string, ru: string) => lang === 'uz' ? uz : ru;

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem(ADMIN_SESSION_KEY, 'authenticated');
      setError('');
    } else {
      setError(t('Email yoki parol noto\'g\'ri', 'Неверный email или пароль'));
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    setEmail('');
    setPassword('');
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(lang === 'uz' ? 'uz-UZ' : 'ru-RU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'uz' ? 'uz-UZ' : 'ru-RU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  };

  const resetPostForm = () => {
    setPostTitleUz('');
    setPostTitleRu('');
    setPostContentUz('');
    setPostContentRu('');
    setPostCategory('news');
    setPostImageUrl('');
    setPostPublishedAt('');
    setPostIsPublished(true);
    setPostFormError('');
    setEditingPostId(null);
  };

  const handleOpenPostForm = () => {
    resetPostForm();
    setShowPostForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEditPost = (post: Post) => {
    setEditingPostId(post.id);
    setPostTitleUz(post.titleUz);
    setPostTitleRu(post.titleRu);
    setPostContentUz(post.contentUz);
    setPostContentRu(post.contentRu);
    setPostCategory(post.category);
    setPostImageUrl(post.imageUrl);
    setPostPublishedAt(post.publishedAt);
    setPostIsPublished(post.isPublished);
    setPostFormError('');
    setShowPostForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSubmitPost = () => {
    if (!postTitleUz.trim() || !postTitleRu.trim()) {
      setPostFormError(t('Ikkala tildagi sarlavha majburiy', 'Заголовок на обоих языках обязателен'));
      return;
    }
    if (!postContentUz.trim() || !postContentRu.trim()) {
      setPostFormError(t('Ikkala tildagi matn majburiy', 'Текст на обоих языках обязателен'));
      return;
    }
    if (!postPublishedAt) {
      setPostFormError(t('Nashr sanasini kiriting', 'Укажите дату публикации'));
      return;
    }

    if (editingPostId) {
      // Update existing post
      const updated = posts.map(post => 
        post.id === editingPostId 
          ? {
              ...post,
              titleUz: postTitleUz.trim(),
              titleRu: postTitleRu.trim(),
              contentUz: postContentUz.trim(),
              contentRu: postContentRu.trim(),
              category: postCategory,
              imageUrl: postImageUrl.trim(),
              publishedAt: postPublishedAt,
              isPublished: postIsPublished,
            }
          : post
      );
      setPosts(updated);
      localStorage.setItem(POSTS_LIST_KEY, JSON.stringify(updated));
    } else {
      // Create new post
      const newPost: Post = {
        id: `post_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        titleUz: postTitleUz.trim(),
        titleRu: postTitleRu.trim(),
        contentUz: postContentUz.trim(),
        contentRu: postContentRu.trim(),
        category: postCategory,
        imageUrl: postImageUrl.trim(),
        publishedAt: postPublishedAt,
        createdAt: new Date().toISOString(),
        isPublished: postIsPublished,
      };

      const updated = [newPost, ...posts];
      setPosts(updated);
      localStorage.setItem(POSTS_LIST_KEY, JSON.stringify(updated));
    }

    setShowPostForm(false);
    resetPostForm();
    setPostSuccess(true);
    setTimeout(() => setPostSuccess(false), 3000);
  };

  const handleDeletePost = (id: string) => {
    const updated = posts.filter(p => p.id !== id);
    setPosts(updated);
    localStorage.setItem(POSTS_LIST_KEY, JSON.stringify(updated));
    setDeleteConfirmId(null);
  };

  const handleTogglePublish = (id: string) => {
    const updated = posts.map(post =>
      post.id === id ? { ...post, isPublished: !post.isPublished } : post
    );
    setPosts(updated);
    localStorage.setItem(POSTS_LIST_KEY, JSON.stringify(updated));
  };

  // ─── Login Screen ───────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <div className="flex justify-end mb-4">
              <div className="flex bg-gray-100 rounded-full p-1">
                <button onClick={() => setLang('uz')} className={`px-4 py-1 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${lang === 'uz' ? 'bg-white shadow text-teal-600' : 'text-gray-500'}`}>UZ</button>
                <button onClick={() => setLang('ru')} className={`px-4 py-1 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${lang === 'ru' ? 'bg-white shadow text-teal-600' : 'text-gray-500'}`}>RU</button>
              </div>
            </div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-user-line text-3xl text-white"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('Administrator paneli', 'Панель администратора')}</h1>
              <p className="text-gray-500 text-sm">{t('Davom etish uchun tizimga kiring', 'Войдите в систему для продолжения')}</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm" placeholder="admin@example.com" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Parol', 'Пароль')}</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm" placeholder={t('Parolni kiriting', 'Введите пароль')} required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  <span className="text-sm">{error}</span>
                </div>
              )}
              <button type="submit" className="w-full bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                {t('Kirish', 'Войти')}
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Dashboard ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* Top Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-5 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
              <i className="ri-shield-user-fill text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">{t('Administrator paneli', 'Панель администратора')}</h1>
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                <i className="ri-mail-line text-teal-400"></i>{ADMIN_EMAIL}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-full p-1">
              <button onClick={() => setLang('uz')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${lang === 'uz' ? 'bg-white shadow text-teal-600' : 'text-gray-500'}`}>UZ</button>
              <button onClick={() => setLang('ru')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${lang === 'ru' ? 'bg-white shadow text-teal-600' : 'text-gray-500'}`}>RU</button>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium">
              <i className="ri-logout-box-line"></i>{t('Chiqish', 'Выйти')}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-teal-50 rounded-lg flex items-center justify-center">
                <i className="ri-user-add-line text-xl text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t('Jami foydalanuvchilar', 'Всего пользователей')}</p>
                <p className="text-2xl font-bold text-gray-800">{users.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-indigo-50 rounded-lg flex items-center justify-center">
                <i className="ri-article-line text-xl text-indigo-500"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t('Jami postlar', 'Всего публикаций')}</p>
                <p className="text-2xl font-bold text-gray-800">{posts.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-green-50 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-xl text-green-500"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t('Nashr etilgan', 'Опубликовано')}</p>
                <p className="text-2xl font-bold text-gray-800">{posts.filter(p => p.isPublished).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === 'users' ? 'bg-teal-500 text-white shadow-md shadow-teal-200' : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-300 hover:text-teal-600'}`}
          >
            <i className="ri-group-line text-base"></i>
            {t('Foydalanuvchilar', 'Пользователи')}
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'users' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{users.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === 'posts' ? 'bg-teal-500 text-white shadow-md shadow-teal-200' : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-300 hover:text-teal-600'}`}
          >
            <i className="ri-article-line text-base"></i>
            {t('Postlar', 'Публикации')}
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'posts' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{posts.length}</span>
          </button>
        </div>

        {/* ── Users Tab ── */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="relative max-w-md">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('Ism yoki email bo\'yicha qidirish...', 'Поиск по имени или email...')}
                  className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">№</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Ism', 'Имя')}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Telefon', 'Телефон')}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Ro\'yxatdan o\'тgan sana', 'Дата регистрации')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-20 text-center text-gray-400">
                        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
                          <i className="ri-user-search-line text-5xl text-gray-200"></i>
                        </div>
                        <p className="text-sm">{t('Foydalanuvchilar topilmadi', 'Пользователи не найдены')}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <tr key={user.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-400">{index + 1}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
                              <i className="ri-user-line text-teal-600 text-sm"></i>
                            </div>
                            <span className="font-medium text-gray-800 text-sm">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{user.phone}</td>
                        <td className="px-6 py-4 text-sm text-gray-400">{formatDate(user.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Posts Tab ── */}
        {activeTab === 'posts' && (
          <div className="space-y-6">
            {/* Success message */}
            {postSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-4 rounded-xl flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-xl"></i>
                <span className="text-sm font-medium">
                  {editingPostId
                    ? t('Post muvaffaqiyatli yangilandi!', 'Публикация успешно обновлена!')
                    : t('Post muvaffaqiyatli qo\'shildi!', 'Публикация успешно добавлена!')
                  }
                </span>
              </div>
            )}

            {/* Action bar */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t('Jami', 'Всего')}: <span className="font-semibold text-gray-800">{posts.length}</span> {t('ta post', 'публикаций')}
              </p>
              <button
                onClick={handleOpenPostForm}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium shadow-sm shadow-teal-200"
              >
                <i className="ri-add-line text-base"></i>
                {t('Yangi post qo\'shish', 'Добавить публикацию')}
              </button>
            </div>

            {/* Post creation/edit form */}
            {showPostForm && (
              <div ref={formRef} className="bg-white rounded-2xl shadow-sm border border-teal-100 overflow-hidden">
                <div className="bg-teal-500 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <i className={editingPostId ? "ri-edit-line" : "ri-add-line"}></i>
                    {editingPostId
                      ? t('Postni tahrirlash', 'Редактирование публикации')
                      : t('Yangi post yaratish', 'Создание новой публикации')
                    }
                  </h3>
                  <button onClick={() => { setShowPostForm(false); resetPostForm(); }} className="text-white/70 hover:text-white cursor-pointer transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="p-6 space-y-5">
                  {/* Category, Date & Publish Status */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Kategoriya', 'Категория')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={postCategory}
                        onChange={(e) => setPostCategory(e.target.value as Post['category'])}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="news">{t('Yangilik', 'Новость')}</option>
                        <option value="announcement">{t('E\'lon', 'Объявление')}</option>
                        <option value="event">{t('Tadbir', 'Мероприятие')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Nashr sanasi', 'Дата публикации')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={postPublishedAt}
                        onChange={(e) => setPostPublishedAt(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Holat', 'Статус')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setPostIsPublished(!postIsPublished)}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center justify-center gap-2 ${
                          postIsPublished
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className={postIsPublished ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                        {postIsPublished
                          ? t('Nashr etilgan', 'Опубликовано')
                          : t('Qoralama', 'Черновик')
                        }
                      </button>
                    </div>
                  </div>

                  {/* Titles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Sarlavha (UZ)', 'Заголовок (UZ)')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={postTitleUz}
                        onChange={(e) => setPostTitleUz(e.target.value)}
                        placeholder={t('O\'zbekcha sarlavha...', 'Заголовок на узбекском...')}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Sarlavha (RU)', 'Заголовок (RU)')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={postTitleRu}
                        onChange={(e) => setPostTitleRu(e.target.value)}
                        placeholder={t('Ruscha sarlavha...', 'Заголовок на русском...')}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Rich Text Contents */}
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Matn (UZ)', 'Текст (UZ)')} <span className="text-red-500">*</span>
                      </label>
                      <RichTextEditor
                        key={`uz-${editingPostId ?? 'new'}`}
                        value={postContentUz}
                        onChange={setPostContentUz}
                        placeholder={t('O\'zbekcha matn kiriting...', 'Введите текст на узбекском...')}
                        lang={lang}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Matn (RU)', 'Текст (RU)')} <span className="text-red-500">*</span>
                      </label>
                      <RichTextEditor
                        key={`ru-${editingPostId ?? 'new'}`}
                        value={postContentRu}
                        onChange={setPostContentRu}
                        placeholder={t('Ruscha matn kiriting...', 'Введите текст на русском...')}
                        lang={lang}
                      />
                    </div>
                  </div>

                  {/* Image URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Muqova rasm URL (ixtiyoriy)', 'URL обложки (необязательно)')}
                    </label>
                    <div className="relative">
                      <i className="ri-image-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="url"
                        value={postImageUrl}
                        onChange={(e) => setPostImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    {postImageUrl && (
                      <div className="mt-3 w-full h-40 rounded-lg overflow-hidden border border-gray-200">
                        <img src={postImageUrl} alt="preview" className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    )}
                  </div>

                  {/* Error */}
                  {postFormError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      <span className="text-sm">{postFormError}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button onClick={() => { setShowPostForm(false); resetPostForm(); }} className="px-6 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium">
                      {t('Bekor qilish', 'Отмена')}
                    </button>
                    <button onClick={handleSubmitPost} className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium flex items-center gap-2">
                      <i className={editingPostId ? "ri-save-line" : "ri-send-plane-line"}></i>
                      {editingPostId
                        ? t('Saqlash', 'Сохранить')
                        : t('Nashr qilish', 'Опубликовать')
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Posts list */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {posts.length === 0 ? (
                <div className="p-16 text-center">
                  <div className="w-20 h-20 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <i className="ri-article-line text-4xl text-teal-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('Hali postlar yo\'q', 'Публикаций пока нет')}</h3>
                  <p className="text-sm text-gray-400">{t('Yangi post qo\'shish tugmasini bosing', 'Нажмите кнопку добавления публикации')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">№</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Sarlavha', 'Заголовок')}</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Kategoriya', 'Категория')}</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Holat', 'Статус')}</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Nashr sanasi', 'Дата публикации')}</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Rasm', 'Изображение')}</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Amallar', 'Действия')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {posts.map((post, index) => (
                        <tr key={post.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-400">{index + 1}</td>
                          <td className="px-6 py-4 max-w-xs">
                            <p className="font-medium text-gray-800 text-sm truncate">{lang === 'uz' ? post.titleUz : post.titleRu}</p>
                            <p className="text-xs text-gray-400 truncate mt-0.5" dangerouslySetInnerHTML={{ __html: (lang === 'uz' ? post.contentUz : post.contentRu).replace(/<[^>]*>/g, '').slice(0, 80) }} />
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[post.category]}`}>
                              {CATEGORY_LABELS[post.category][lang]}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleTogglePublish(post.id)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                                post.isPublished
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <i className={post.isPublished ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                              {post.isPublished
                                ? t('Nashr', 'Опубл.')
                                : t('Qoralama', 'Черновик')
                              }
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatDateOnly(post.publishedAt)}</td>
                          <td className="px-6 py-4">
                            {post.imageUrl ? (
                              <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                                <img src={post.imageUrl} alt="" className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">{t('Yo\'q', 'Нет')}</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {deleteConfirmId === post.id ? (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleDeletePost(post.id)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer">
                                  {t('Ha, o\'chirish', 'Да, удалить')}
                                </button>
                                <button onClick={() => setDeleteConfirmId(null)} className="px-3 py-1.5 border border-gray-200 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                                  {t('Bekor', 'Отмена')}
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEditPost(post)}
                                  className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                                  title={t('Tahrirlash', 'Редактировать')}
                                >
                                  <i className="ri-edit-line text-base"></i>
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(post.id)}
                                  className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                  title={t('O\'chirish', 'Удалить')}
                                >
                                  <i className="ri-delete-bin-line text-base"></i>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}